import {useBalanceHistoryQuery, DepositoryType} from '@transactions/graphql';
import Money from 'common/Money';
import * as React from 'react';
import {
  formatDepositoryType,
  useAccountOutletContext,
} from 'src/account/AccountDetail';
import {describeBalance} from 'src/account/balanceDisplay';
import {BalanceHistoryChart} from 'src/account/BalanceHistoryChart';
import {InvestmentOverviewView} from 'src/account/InvestmentOverviewView';
import {PlaidCard} from 'src/account/PlaidCard';
import {AmountDisplay} from 'src/AmountDisplay';
import {useCurrencyLabel} from 'src/hooks/useUnits';
import type {DepositoryAccount} from 'src/account/AccountDetail';

/**
 * Overview tab of the account detail page (the index route). Dispatches on
 * account type: depository accounts get the balance-focused overview below,
 * investment accounts get the portfolio-focused one.
 */
export const AccountOverviewView = () => {
  const {account, refetchAccount} = useAccountOutletContext();
  if (account.__typename === 'InvestmentAccount') {
    return (
      <InvestmentOverviewView account={account} refetchAccount={refetchAccount} />
    );
  }
  return (
    <DepositoryOverviewView account={account} refetchAccount={refetchAccount} />
  );
};

type DepositoryOverviewProps = {
  account: DepositoryAccount;
  refetchAccount: () => void;
};

/**
 * Depository overview: the account facts that used to crowd the shared
 * header (type, currency, transaction count, current balance) plus the
 * balance history chart. Recording and reconciling balances live on the
 * Reconcile tab; the Plaid connection, its synced readings, and its raw data
 * live in the collapsible Plaid card below (Plaid-linked accounts only).
 */
const DepositoryOverviewView = ({
  account,
  refetchAccount,
}: DepositoryOverviewProps) => {
  // The chart's full series is assembled server-side (readings + reconciled
  // walks + estimated tails + anchors), so the client just renders it.
  const {
    data: historyData,
    loading: historyLoading,
    refetch: refetchHistory,
  } = useBalanceHistoryQuery({
    variables: {accountId: account._id},
    fetchPolicy: 'cache-and-network',
  });

  const history = historyData?.balanceHistory;

  const currencyLabel = useCurrencyLabel();
  const isCreditCard = account.depositoryType === DepositoryType.CreditCard;
  const headline = describeBalance(account.balance, isCreditCard);
  const currentBalance = Money.fromServer({
    amount: headline.amount,
    currency: account.currency,
  });

  return (
    <div className="flex flex-1 flex-col gap-6 overflow-y-auto p-4 sm:p-8">
      {/* Account facts */}
      <div className="border-border bg-card flex flex-wrap items-start justify-between gap-x-6 gap-y-2 rounded-xl border px-4 py-3">
        <div className="min-w-0">
          <p className="text-muted-foreground m-0 text-sm">
            {formatDepositoryType(account.depositoryType)} ·{' '}
            {currencyLabel(account.currency)}{' '}
            · {account.transactionCount.toLocaleString()}{' '}
            {account.transactionCount === 1 ? 'transaction' : 'transactions'}
          </p>
        </div>
        <div className="text-right">
          <p className="text-muted-foreground m-0 text-xs uppercase tracking-wide">
            {headline.label}
          </p>
          <AmountDisplay amount={currentBalance} variant="plain" align="right" />
          {account.balanceStaleSince && (
            <p className="text-muted-foreground mt-0.5 text-xs">
              as of {account.balanceStaleSince}
            </p>
          )}
        </div>
      </div>

      {/* Balance history chart. Reduced side padding on mobile so the plot
          uses the full width of the narrow screen. */}
      <div className="bg-card border-border rounded-xl border px-2 py-3 sm:p-4">
        <BalanceHistoryChart
          points={history?.points ?? []}
          spans={history?.spans ?? []}
          loading={historyLoading}
          currency={account.currency}
          isCreditCard={isCreditCard}
        />
      </div>

      {/* Plaid card: connection status, synced readings, and raw sync JSON.
          Renders only for Plaid-linked accounts; its data loads lazily. */}
      {account.plaid && (
        <PlaidCard
          account={account}
          onReadingChanged={() => {
            void refetchHistory();
            // Promoting the newest reading updates Account.balance server-side.
            refetchAccount();
          }}
        />
      )}
    </div>
  );
};
